/**
 * WombVisualEngine — Circular flow of warm light in lower abdomen
 *
 * Visual: Deep amber/ochre circular warmth, slow rotating flow
 * Very faint outline of lower torso area (5-8% opacity, heavily blurred)
 * Feel: nurturing, grounding, creative, held
 *
 * Color: Deep amber → ochre → warm brown
 * Position: lower-center (60% from top)
 * Movement: slow circular rotation, gentle expansion
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';

const WombVisualEngine = ({ elapsed = 0, totalDuration = 420, isActive = false }) => {
  const [breath, setBreath] = useState(0.5);
  const [pulse, setPulse] = useState(0.5);
  const [rotation, setRotation] = useState(0);
  const [depthDrift, setDepthDrift] = useState(1);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;
    startRef.current = Date.now();
    const tick = () => {
      const now = Date.now() - startRef.current;
      setBreath(0.5 + 0.5 * Math.sin((now % 14000) / 14000 * Math.PI * 2 - Math.PI / 2));
      setPulse(0.5 + 0.5 * Math.sin((now % 11000) / 11000 * Math.PI * 2 - Math.PI / 2));
      setRotation((now % 40000) / 40000 * 360);
      setDepthDrift(1 + 0.012 * Math.sin((now % 20000) / 20000 * Math.PI * 2));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive]);

  const phase = useMemo(() => {
    if (elapsed < 60) return 'arrival';
    if (elapsed < 180) return 'expansion';
    if (elapsed < 300) return 'warmth';
    return 'integration';
  }, [elapsed]);

  const intensity = useMemo(() => {
    if (phase === 'arrival') return (0.3 + Math.min(1, elapsed / 60) * 0.3) + breath * 0.08;
    if (phase === 'expansion') return (0.6 + ((elapsed - 60) / 120) * 0.25) + breath * 0.1;
    if (phase === 'warmth') return 0.9 + breath * 0.06;
    return 0.92 + breath * 0.04;
  }, [phase, elapsed, breath]);

  const expansion = useMemo(() => {
    if (phase === 'arrival') return 0.3 + Math.min(1, elapsed / 60) * 0.2;
    if (phase === 'expansion') return 0.5 + ((elapsed - 60) / 120) * 0.3;
    if (phase === 'warmth') return 0.8 + ((elapsed - 180) / 120) * 0.12;
    return 0.92;
  }, [phase, elapsed]);

  const cX = '50%';
  const cY = '60%';

  return (
    <motion.div className="absolute inset-0 overflow-hidden" data-testid="womb-visual-engine"
      style={{ transform: `scale(${depthDrift})`, transformOrigin: `50% 60%` }}>

      {/* Deep warm darkness */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse 100% 90% at 50% 60%, rgba(35,22,12,${0.5 + intensity * 0.3}) 0%, rgba(10,7,4,1) 60%)`,
      }} />

      {/* Atmosphere */}
      <motion.div className="absolute inset-0 pointer-events-none"
        animate={{ opacity: 0.04 + breath * 0.05 + intensity * 0.04 }}
        transition={{ duration: 3, ease: 'easeInOut' }}
        style={{ background: `radial-gradient(ellipse 85% 70% at 50% 60%, rgba(200,155,90,0.15) 0%, transparent 60%)` }} />

      {/* Very faint lower torso outline — abstract curves, not anatomical */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ paddingTop: '20vh' }}>
        <motion.svg viewBox="0 0 200 200" style={{ width: '280px', height: '280px', filter: 'blur(18px)' }}
          animate={{ opacity: 0.05 + breath * 0.02 }} transition={{ duration: 3 }}>
          <ellipse cx="100" cy="100" rx="65" ry="55" fill="none" stroke="rgba(180,140,85,0.08)" strokeWidth="1.5" />
          <ellipse cx="100" cy="95" rx="45" ry="40" fill="none" stroke="rgba(180,140,85,0.05)" strokeWidth="1" />
        </motion.svg>
      </div>

      {/* Outer warm field */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: cX, top: cY, transform: 'translate(-50%, -50%)' }}
        animate={{
          width: 350 + expansion * 400 + pulse * 50 + breath * 30,
          height: 300 + expansion * 340 + pulse * 40 + breath * 25,
          opacity: 0.1 + intensity * 0.22 + pulse * 0.05,
        }}
        transition={{ duration: 2.5, ease: 'easeInOut' }}>
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(ellipse, rgba(190,145,80,0.18) 0%, rgba(175,130,70,0.05) 40%, transparent 65%)',
          filter: 'blur(35px)',
        }} />
      </motion.div>

      {/* Rotating circular flow — the signature womb movement */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: cX, top: cY, transform: `translate(-50%, -50%) rotate(${rotation}deg)` }}>
        <motion.div animate={{
          width: 200 + expansion * 180 + pulse * 30,
          height: 170 + expansion * 150 + pulse * 25,
          opacity: 0.15 + intensity * 0.35 + pulse * 0.08,
        }} transition={{ duration: 2, ease: 'easeInOut' }}>
          <div className="w-full h-full rounded-full" style={{
            background: `conic-gradient(from 0deg, rgba(210,160,85,${0.3 + pulse * 0.1}), rgba(185,135,70,0.05), rgba(200,150,80,${0.25 + pulse * 0.08}), rgba(185,135,70,0.05))`,
            filter: 'blur(12px)',
          }} />
        </motion.div>
      </motion.div>

      {/* Mid glow — warm amber */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: cX, top: cY, transform: 'translate(-50%, -50%)' }}
        animate={{
          width: 120 + expansion * 130 + pulse * 22 + breath * 12,
          height: 100 + expansion * 110 + pulse * 18 + breath * 10,
          opacity: 0.3 + intensity * 0.45 + pulse * 0.08,
        }}
        transition={{ duration: 1.8, ease: 'easeInOut' }}>
        <div className="w-full h-full rounded-full" style={{
          background: `radial-gradient(ellipse 85% 80% at 50% 50%, rgba(225,180,110,${0.7 + pulse * 0.1}) 0%, rgba(210,165,95,${0.25}) 35%, transparent 65%)`,
          filter: 'blur(5px)',
        }} />
      </motion.div>

      {/* Core — deep ochre warmth */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: cX, top: cY, transform: 'translate(-50%, -50%)' }}
        animate={{
          width: 40 + expansion * 45 + pulse * 12,
          height: 35 + expansion * 38 + pulse * 10,
          opacity: 0.45 + intensity * 0.4 + pulse * 0.1,
        }}
        transition={{ duration: 1.4, ease: 'easeInOut' }}>
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, rgba(255,225,170,0.9) 0%, rgba(240,200,140,0.45) 35%, rgba(220,175,115,0.12) 60%, transparent 75%)',
        }} />
      </motion.div>

      {/* CONNECTION LAYER — glow extends beyond body area */}
      {(phase === 'warmth' || phase === 'integration') && (
        <motion.div className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{
            opacity: phase === 'warmth'
              ? Math.min(0.3, ((elapsed - 180) / 120) * 0.3) * (0.85 + breath * 0.15)
              : 0.3 + Math.min(0.2, ((elapsed - 300) / 120) * 0.2) * (0.85 + breath * 0.15),
          }}
          transition={{ duration: 6, ease: 'easeInOut' }}>
          <div className="absolute inset-0" style={{
            background: `radial-gradient(ellipse 90% 85% at 50% 60%, rgba(220,175,105,0.18) 0%, rgba(200,155,85,0.06) 30%, rgba(185,140,70,0.02) 50%, transparent 65%)`,
          }} />
        </motion.div>
      )}

      {/* Integration */}
      {phase === 'integration' && (
        <motion.div className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse 60% 50% at 50% 60%, rgba(220,175,110,0.2) 0%, transparent 50%)` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: Math.min(0.5, ((elapsed - 300) / 120) * 0.5) * (0.8 + breath * 0.2) }}
          transition={{ duration: 5 }} />
      )}

      {/* Particles */}
      {[...Array(8)].map((_, i) => (
        <motion.div key={`p-${i}`} className="absolute rounded-full pointer-events-none"
          style={{ width: 2 + (i % 3) * 0.8, height: 2 + (i % 3) * 0.8, background: `rgba(210,165,100,${0.08 + (i % 3) * 0.04})`,
            left: `${30 + (i * 6) % 40}%`, top: `${40 + (i * 5) % 30}%` }}
          animate={{ y: [0, -8 - (i % 3) * 3, 0], x: [0, -3 + (i % 2) * 5, 0], opacity: [0, 0.1 + intensity * 0.07, 0] }}
          transition={{ duration: 14 + (i % 3) * 3, repeat: Infinity, delay: i * 2, ease: 'easeInOut' }} />
      ))}
    </motion.div>
  );
};

export default WombVisualEngine;
